import { DEFAULT_FAVICON, DEFAULT_LOGO } from "@/lib/brand-assets";
import type { SiteSettings } from "@/features/settings/use-site-settings";

/**
 * Defaults مشتركة بين Client Settings hook وقراء Landing/Server.
 * لا تستورد Firebase أو React حتى تكون آمنة داخل Server Components.
 */
export const DEFAULTS: SiteSettings = {
  siteName: "BacZone",
  logoUrl: DEFAULT_LOGO,
  faviconUrl: DEFAULT_FAVICON,
  heroTitle: "ادرس بذكاء. ونجح في البكالوريا.",
  heroSubtitle: "غرف دراسة مباشرة، مساعدة ذكية، بطاقات مراجعة، ومجتمع تلاميذي نشط — كل ما تحتاجه في مكان واحد.",
  footerText: `© ${new Date().getFullYear()} BacZoneDZ. جميع الحقوق محفوظة.`,
  // تُضاف روابط الفوتر من لوحة الإدارة بعد التحقق منها؛ لا نعيد رابط النطاق القديم افتراضياً.
  footerLinks: [],
  maintenanceMode: false,
  allowRegistration: true,
  sidebar: { enabled: false, widgets: [] },
  sidebarArticles: { enabled: true, mode: "latest", label: "", limit: 4 },
  advertiseEnabled: true,

  landingBadge: "صُنعت في الجزائر خصيصاً لتلاميذ البكالوريا",
  heroTitleLine1: "ادرس أذكى، راجع أسرع،",
  heroTitleLine2: "وانجح في البكالوريا",
  heroCtaPrimary: "أنشئ حسابك وابدأ",
  heroCtaSecondary: "دخول",
  badges: [
    { id: "b1", icon: "ban", label: "تجربة دراسة بلا تشتيت متعمد" },
    { id: "b2", icon: "signal", label: "مصمم للهاتف والاتصالات المحدودة" },
    { id: "b3", icon: "users", label: "مساحة لتلاميذ وأساتذة الجزائر" },
    { id: "b4", icon: "flag", label: "صُنعت في الجزائر 🇩🇿" },
  ],
  stepsTitle: "ابدأ في 3 خطوات",
  steps: [
    { id: "s1", n: "01", title: "أنشئ حسابك", desc: "سجّل بالبريد الإلكتروني، ثم اختر شعبتك وابدأ من المسار الذي تحتاجه." },
    { id: "s2", n: "02", title: "انضم أو أنشئ غرفة/مجموعة", desc: "ابحث عن مجموعة شعبتك أو أنشئ غرفة مع أصدقائك وابدأ المراجعة." },
    { id: "s3", n: "03", title: "تعلّم، شارك، وتقدّم", desc: "استخدم السبورة والصوت والخباشة، واكسب النقاط لترقى في الترتيب." },
  ],
  audienceTitle: "لمن هذه المنصّة؟",
  audienceSubtitle: "تجربة مختلفة لكل دور",
  audience: [
    {
      id: "a1", icon: "users", title: "للتلميذ",
      desc: "احضر حصصاً مباشرة على سبورة تفاعلية، حلّ تحدّيات الأستاذ في مساحتك الخاصة، احفظ أي فكرة كبطاقة مراجعة بضغطة، واسأل دون أن يظهر اسمك. تابع تقدّمك وراجع بالبطاقات ومؤقّت التركيز.",
    },
    {
      id: "a2", icon: "chalkboard", title: "للأستاذ",
      desc: "أنشئ غرفة في ثوانٍ، اشرح على السبورة مع الصوت والملفات، اطرح تحدّياً وشاهد حلول كل التلاميذ في لوحة واحدة، وأنهِ الحصة بملخّص تلقائي. انشر ملخّصاتك واحصل على تقييم تلاميذك.",
    },
  ],
  pricingTitle: "كم تكلّف؟",
  pricingNote: "إنشاء الحساب والمجتمع وأدوات المراجعة متاحة لكل التلاميذ. أما الغرف والملخّصات التي يضعها الأساتذة فبعضها مدفوع بسعر يحدّده صاحبها، ويُفتح بكود وصول بعد الدفع.",
  pricingRows: [
    { id: "p1", title: "الحساب والمجتمع", desc: "التسجيل، المنشورات، الرسائل، الأصدقاء، والإشعارات." },
    { id: "p2", title: "أدوات المراجعة", desc: "بطاقات المراجعة، المهام، متتبّع الدراسة، ومؤقّت التركيز." },
    { id: "p3", title: "الغرف والملخّصات", desc: "منها المفتوح للجميع ومنها المدفوع بسعر يحدّده الأستاذ." },
  ],
  faqTitle: "أسئلة شائعة",
  faq: [
    { id: "q1", q: "هل أحتاج حاسوباً؟", a: "لا. المنصّة مصمّمة للهاتف أولاً، وتعمل على شبكات 3G و4G." },
    { id: "q2", q: "كيف أدفع مقابل غرفة أو ملخّص مدفوع؟", a: "تتواصل مع الإدارة داخل المنصّة عبر دردشة الدفع، ثم تحصل على كود وصول تُدخله ليُفتح المحتوى." },
    { id: "q3", q: "هل يمكنني أن أدرّس على المنصّة؟", a: "نعم. تواصل مع الإدارة لتفعيل حساب أستاذ، فتستطيع إنشاء الغرف ونشر الملخّصات." },
    { id: "q4", q: "ماذا يحدث لملاحظاتي بعد الحصة؟", a: "ما تحفظه أثناء الحصة يصبح بطاقات مراجعة في حسابك، ويصلك ملخّص الحصة إن نشره الأستاذ." },
  ],
  featuresTitle: "كل ما تحتاجه للنجاح",
  featuresSubtitle: "أدوات احترافية في مكان واحد",
  features: [
    { id: "f1", icon: "chalkboard", title: "غرف دراسة تفاعلية", desc: "سبورة ذكية بأدوات الأستاذ، فيديو YouTube متزامن، دردشة جماعية، ورفع الملفات." },
    { id: "f2", icon: "microphone", title: "صوت جماعي للغرف", desc: "تواصل صوتياً داخل الغرفة عندما تحتاج إلى شرح أو مراجعة مشتركة؛ وقد تتأثر الجودة بالمتصفح والاتصال." },
    { id: "f3", icon: "layers", title: "مجموعات المواد", desc: "مجموعة لكل مادة أو شعبة فيها نقاش مستمر، قائمة أعضاء، وملفات مشتركة." },
    { id: "f4", icon: "folder", title: "مشاركة الملفات بـ Google Drive", desc: "ارفع ملفاتك PDF/Word/Excel/PPT واعرضها داخل المنصّة مباشرةً بلا تحميل." },
    { id: "f5", icon: "robot", title: "الخباشة — مساعدتك الآلية", desc: "يطرح أسئلة توضيحية، يضع خطط مراجعة مخصّصة حسب شعبتك، ويطمئنك ويشجّعك." },
    { id: "f6", icon: "trophy", title: "نظام إنجازات ومنافسة", desc: "نقاط على كل نشاط، مستويات متصاعدة، أوسمة، وترتيب يومي بين التلاميذ." },
    { id: "f7", icon: "users", title: "مجتمع دراسي حقيقي", desc: "منشورات، تعليقات، صداقات، رسائل خاصّة، ومشاركة ملفات الدراسة." },
    { id: "f8", icon: "bell", title: "إشعارات داخل المنصة", desc: "تابع الرسائل وطلبات الصداقة والتنبيهات من مساحة الإشعارات داخل حسابك؛ وقد تختلف التنبيهات خارج المنصة حسب إعدادات المتصفح." },
    { id: "f9", icon: "clock", title: "مؤقّت بومودورو", desc: "قسّم جلسة المراجعة إلى فترات تركيز واستراحة قابلة للتعديل، ثم استخدمه بما يناسب طاقتك وجدولك." },
  ],
  /* ⚠️ النصّ المطلوب تغييره («انضم لآلاف التلاميذ الجزائريين») غير
     موجود في الشيفرة إطلاقاً — بُحث عنه في `src/` و`public/` كلّها.
     فهو إمّا محرَّر من لوحة الإدارة ومخزَّن في `settings` بقاعدة
     البيانات، وإمّا بقيّة نسخة أقدم.

     الصياغة الجديدة هنا هي **الافتراض** الذي يظهر لمن لا سجلّ له في
     `settings`. أمّا الغرف القائمة التي حُرّر نصّها من اللوحة فيبقى
     المخزَّن هو المعروض حتى يُحدَّث من هناك — قاعدة البيانات تعلو
     على الافتراض بحكم التصميم.

     والصياغة تقصد الطموح لا الإحصاء: «نسعى لنكون» وعدٌ يمكن الوفاء
     به، بخلاف «آلاف التلاميذ» الذي يدّعي عدداً لا سند له. */
  ctaTitle: "جاهز للانطلاق نحو البكالوريا؟",
  ctaSubtitle: "نسعى لنكون أكبر مجتمع دراسي لتلاميذ البكالوريا في الجزائر — ابدأ بما تحتاجه اليوم: أداة للحساب أو التخطيط، دليل للمراجعة، أو مساحة دراسة مشتركة.",
  ctaButton: "اكتشف BacZone",
  landingHeroImageUrl: "/landing/baczone-student-hero.png",
  landingHeroImageAlt: "تلميذ جزائري يراجع دروسه على الحاسوب",
  landingOverviewTitle: "كل ما تحتاجه في مكان واحد",
  landingOverviewSubtitle: "أدوات ومحتوى ومساحات دراسة تساعدك على الاستعداد للبكالوريا خطوةً خطوة.",
  landingToolsTitle: "أدوات ذكية تساعدك على التقدم",
  landingToolsSubtitle: "احسب، خطط، تدرّب وتابع تقدمك باستخدام أدوات BacZone الموجودة فعليًا.",
  landingArticlesTitle: "أحدث المقالات والدروس",
  landingArticlesSubtitle: "محتوى تعليمي قابل للقراءة يساعدك على بناء مراجعة أوضح وأكثر واقعية.",
  landingGuidesTitle: "أدلة ونصائح للمراجعة والتوجيه",
  landingGuidesSubtitle: "أدلة تعود إليها عندما تحتاج شرحًا واضحًا أو خطوة عملية.",
  landingCommunityTitle: "مجتمع دراسي وغرف تفاعلية",
  landingCommunitySubtitle: "مساحة للتعلم الجماعي، والغرف، والملاحظات، والملفات، والتفاعل مع الأساتذة والتلاميذ.",
  landingCoursesTitle: "دورات تعليمية منظمة",
  landingCoursesSubtitle: "تظهر هنا الدورات المنشورة فعلياً، وقد تتضمن فيديو وملفات PDF ودروسًا نصية أو مصادر خارجية بحسب كل دورة.",
  landingFooterDescription: "منصة دراسة تفاعلية لتلاميذ البكالوريا في الجزائر. تجمع الأدوات والمحتوى والأدلة والغرف والمجتمع في تجربة واحدة.",

  homeWelcomeTitle: "مرحباً بعودتك",
  homeWelcomeSubtitle: "ماذا تريد أن تفعل اليوم؟",
  homeArticlesTitle: "مقالات قد تفيدك",
  homeArticleSlugs: [],

  telegramUrl: "https://t.me/baczonedz",
  instagramUrl: "https://www.instagram.com/baczonedz",
  facebookUrl: "https://www.facebook.com/baczonedz",
  paymentUrl: "https://m.me/baczonedz1",
  averageCalcUrl: "https://www.baczonedz.com/p/blog-page_14.html",
  pastExamsUrl: "https://www.baczonedz.com/p/blog-page_9.html",
  weightedCalcUrl: "https://www.baczonedz.com/p/2026.html",
  /* وجهة داخلية: المحاكاة موجودة في الموقع، فلا يُخرَج الزائر منه.
     يبقى الحقل قابلاً للتحرير من لوحة الإدارة. */
  bacSimUrl: "/tools/exam-simulator",
  adsEmail: "saidaouina22@gmail.com",
  adsWhatsapp: "+213657498876",
};
